import React, { useContext } from 'react';
import Playlist from '../../models/playlist/playlist';
import SyncSource from '../../models/playlist/syncSource';
import {
  findYouTubeSource,
  linkYouTubePlaylist,
  unlinkSource,
} from '../../models/playlist/syncSourceHelpers';
import { LibraryContext } from '../../services/library/libraryService';
import { Context } from '../../services/_globalContext/context';
import Constants, { Keys, messages } from '../../settings';
import decodeHtml from '../../utilities/decodeHtml';
import { preventDefaultStopPropogation } from '../../utilities/preventDefault';
import FadilaMenuItem from '../common/contextMenu/fadilaMenuItem';
import FadilaSubMenu from '../common/contextMenu/fadilaSubMenu';
import useYouTubeWriteAccess from '../common/hooks/useYouTubeWriteAccess';
import { showLibraryErrors } from '../../services/library/libraryError';

interface Props {
  youTubePlaylist: gapi.client.youtube.Playlist;
}

/**
 * "Sync with Playlist" on a YouTube playlist's context menu: the user's Fadila
 * playlists, a tick on those already linked to this one (pressing unlinks),
 * and a new playlist named after the YouTube one.
 *
 * Links are two-way by default. The direction is changed afterwards from the
 * Fadila playlist's own menu in the library, where the sync lives.
 */
export default function YouTubeSyncSubMenu({ youTubePlaylist }: Props) {
  const ctx = useContext(Context);
  const libCtx = useContext(LibraryContext);
  const { ensureYouTubeWriteAccess } = useYouTubeWriteAccess();

  const youTubeId = youTubePlaylist.id!;
  const title = decodeHtml(youTubePlaylist.snippet?.title || youTubeId);

  function link(pl: Playlist) {
    // The freshest copy, in case the menu was open across an edit.
    const current = libCtx.playlists.find((x) => x.id === pl.id) || pl;

    libCtx.updatePlaylist(linkYouTubePlaylist(current, youTubeId, title));
    ctx.callSnackbar(messages(Keys.youTubeSyncLinked), false);

    // Two-way needs to write. Popup on desktop; a hint on iOS, where the
    // button on this page does the redirect.
    ensureYouTubeWriteAccess();
  }

  function unlink(pl: Playlist, source: SyncSource) {
    const current = libCtx.playlists.find((x) => x.id === pl.id) || pl;

    libCtx.updatePlaylist(unlinkSource(current, source));
    ctx.callSnackbar(messages(Keys.youTubeSyncUnlinked), false);
  }

  function linkToNewPlaylist() {
    const name = window.prompt('Please enter or confirm new playlist name', title);

    if (name) {
      showLibraryErrors(ctx.callSnackbar, () => link(libCtx.createPlaylist(name)));
    }
  }

  return (
    <FadilaSubMenu
      title="Sync with Playlist"
      preventCloseOnClick
      onClick={(ev: React.MouseEvent) => ev.stopPropagation()}
    >
      {libCtx.playlists.map((pl) => {
        const source = findYouTubeSource(pl, youTubeId);

        return (
          <FadilaMenuItem
            key={pl.id}
            onClick={preventDefaultStopPropogation(() => (source ? unlink(pl, source) : link(pl)))}
            attributes={{
              title: source
                ? 'Synced with this YouTube playlist. Press to unlink.'
                : 'Keep this playlist in sync with the YouTube playlist',
            }}
          >
            {source ? '✓ ' : ''}
            {pl.name}
          </FadilaMenuItem>
        );
      })}
      <FadilaMenuItem onClick={preventDefaultStopPropogation(linkToNewPlaylist)}>
        {Constants.NewPlaylistName}
      </FadilaMenuItem>
    </FadilaSubMenu>
  );
}
